import { Switch, Table, Spin, Empty, Tag, Button, Select, Popconfirm, Input, Modal } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { SortSvgBottom, SortSvgTop } from 'utils/svgs';
import { checkPermission, formatPhoneNumber, toastText } from 'utils/utils';
import {
    updateEmployeeAction,
    updateEmployeeStatusAction,
} from 'redux/action/employeesTableAction';
import { removeEmployeeFromList } from 'redux/slice/employeesTableSlice';
import { putApi, postApi, getApi } from 'redux/apis';
import SearchAndFilter from '../SearchAndFilter';
import './index.scss';

interface EmployeesTableProps {
    performSearchHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
    searchValue: string;
    performFilterHandler: (value: string) => void;
    filterValue: string;
    performSortHandler: (sortBy: string, sortOrder: string) => void;
    sortBy: string;
    sortOrder: string;
    setIsInViewPort: (value: boolean) => void;
}

const EmployeesTable: FC<EmployeesTableProps> = (props) => {
    const { Column } = Table;
    const {
        performSearchHandler,
        searchValue,
        performFilterHandler,
        filterValue,
        performSortHandler,
        sortBy,
        sortOrder,
        setIsInViewPort,
    } = props;

    const dispatch = useDispatch<AppDispatch>();
    const tableRef = useRef<HTMLDivElement>(null);

    const {
        data: employeesData = [],
        total = 0,
        isLoading,
        statusUpdateLoading = {},
        nameUpdateLoading = {},
    } = useSelector((state: any) => state.employeesTable || {});

    // Determine if filters are active
    const hasActiveFilters = Boolean(
        searchValue?.trim() ||
        (filterValue && filterValue !== 'all')
    );

    const CustomEmptyState = () => (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
                isLoading
                    ? null
                    : hasActiveFilters
                        ? "No Employee records match the selected filter criteria"
                        : "No Employee records are currently available"
            }
            style={{
                padding: '40px 20px',
                margin: '20px 0'
            }}
        />
    );

    const selectedCompanyPermission = useSelector(
        (state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
    );

    const isEditPermission = checkPermission(selectedCompanyPermission, {
        permissionName: 'Employees',
        permission: ['edit'],
    });

    // WS-14 (ENG-333): Linked QBO Employee column state. The table's rows come
    // from Redux; QBO link/unlink results are kept as a local override layer
    // applied on top of the Redux data (no Redux action for qbo-link).
    const [linkOverrides, setLinkOverrides] = useState<
        Record<string, { employeeId: string | null; fullName: string }>
    >({});
    // Per-row in-flight flag for the link/unlink PUT (mirrors statusUpdateLoading).
    const [linkLoading, setLinkLoading] = useState<Record<string, boolean>>({});
    // The row currently showing the inline "Link to QBO" Select (at most one).
    const [linkingRowId, setLinkingRowId] = useState<string | null>(null);
    // Lazily-fetched QBO employee roster (only loaded on first "Link to QBO").
    const [qboEmployees, setQboEmployees] = useState<
        Array<{ Id: string; DisplayName: string }>
    >([]);
    const [qboLoading, setQboLoading] = useState(false);

    // ENG-363 (Item 4): inline name editing. At most one row is editable at a
    // time (mirrors the linkingRowId pattern). `draftName` holds the in-progress
    // value; `recentPayPeriod` is the company's most recent pay period, fetched
    // lazily the first time a name edit begins, and shown in the save
    // confirmation so the user knows which period the change applies to.
    const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(
        null
    );
    const [draftName, setDraftName] = useState<string>('');
    const [recentPayPeriod, setRecentPayPeriod] = useState<{
        id: string;
        startDate: string;
        endDate: string;
    } | null>(null);
    // ENG-413: the save confirmation is a controlled <Modal open> (mirrors the
    // EC grid EmployeeEditModal) instead of the imperative Modal.confirm().
    // `nameConfirm` holds the pending edit while the dialog is shown; because
    // `open` is derived from state, setting it is idempotent — no repeated
    // trigger can stack or reopen a second dialog (the ENG-413 root cause).
    // `nameSaving` drives the OK button's loading state during the async PUT.
    const [nameConfirm, setNameConfirm] = useState<{
        employee: any;
        trimmedName: string;
    } | null>(null);
    const [nameSaving, setNameSaving] = useState(false);

    // Lazy-load the QBO roster the first time a user starts linking, then open the
    // inline Select for that row.
    const startLinking = async (employeeRowId: string) => {
        setLinkingRowId(employeeRowId);
        if (qboEmployees.length > 0) {
            return;
        }
        setQboLoading(true);
        try {
            const res = await postApi('/quickbooks/employees', {
                companyId: localStorage.getItem('companyId') || '',
            });
            setQboEmployees(res.data?.data ?? []);
        } catch {
            toastText('Failed to load QuickBooks employees. Please try again.', 'error');
            setLinkingRowId(null);
        } finally {
            setQboLoading(false);
        }
    };

    // Link a CAP employee to the selected QBO employee. On 409 the QBO employee is
    // already linked elsewhere; surface a specific message.
    const handleLink = async (employee: any, qboId: string) => {
        const qboEmp = qboEmployees.find((e) => e.Id === qboId);
        setLinkLoading((prev) => ({ ...prev, [employee.id]: true }));
        try {
            await putApi(`/employees/${employee.id}/qbo-link`, {
                qboEmployeeId: qboId,
            });
            setLinkOverrides((prev) => ({
                ...prev,
                [employee.id]: {
                    employeeId: qboId,
                    fullName: qboEmp?.DisplayName ?? employee.fullName,
                },
            }));
            setLinkingRowId(null);
            toastText('Employee linked to QuickBooks', 'success');
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toastText(
                    'This QBO employee is already linked to another CAP employee.',
                    'error'
                );
            } else {
                toastText('Failed to link to QuickBooks. Please try again.', 'error');
            }
        } finally {
            setLinkLoading((prev) => ({ ...prev, [employee.id]: false }));
        }
    };

    // Unlink a CAP employee from QBO (clears Employee.employeeId server-side).
    const handleUnlink = async (employee: any) => {
        setLinkLoading((prev) => ({ ...prev, [employee.id]: true }));
        try {
            await putApi(`/employees/${employee.id}/qbo-link`, {
                qboEmployeeId: null,
            });
            setLinkOverrides((prev) => ({
                ...prev,
                [employee.id]: { employeeId: null, fullName: employee.fullName },
            }));
            toastText('Employee unlinked from QuickBooks', 'success');
        } catch {
            toastText('Failed to unlink from QuickBooks. Please try again.', 'error');
        } finally {
            setLinkLoading((prev) => ({ ...prev, [employee.id]: false }));
        }
    };

    // Infinite scroll handler
    const scrollHandler = useCallback((event: any) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setIsInViewPort(true);
        } else {
            setIsInViewPort(false);
        }
    }, [setIsInViewPort]);

    useEffect(() => {
        if (tableRef.current) {
            const tableBody = tableRef.current.querySelector('.ant-table-body');
            if (tableBody) {
                tableBody.addEventListener('scroll', scrollHandler);
                return () => {
                    tableBody.removeEventListener('scroll', scrollHandler);
                };
            }
        }
    }, [employeesData, scrollHandler]);

    // Handle status update
    const handleStatusUpdate = async (checked: boolean, employee: any) => {
        try {
            await dispatch(
                updateEmployeeStatusAction({
                    companyId: localStorage.getItem('companyId') || '',
                    employeeId: employee.id,
                    isActiveInApp: checked,
                })
            ).unwrap();

            // Remove employee from list if it doesn't match the current filter
            // Wait 500ms so user can see the update before it's removed
            if (filterValue === 'active' && !checked) {
                setTimeout(() => {
                    dispatch(removeEmployeeFromList(employee.id));
                }, 500);
            } else if (filterValue === 'inactive' && checked) {
                setTimeout(() => {
                    dispatch(removeEmployeeFromList(employee.id));
                }, 500);
            }
        } catch (error: any) {
            toastText(
                error?.message || 'Failed to update employee status',
                'error'
            );
        }
    };

    // ENG-363 (Item 4): format a pay period's date range for the confirmation
    // dialog, e.g. "Jun 1, 2026 – Jun 15, 2026".
    const formatDate = (value: string) =>
        new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

    // Begin editing a row's name. Lazily fetch the most recent pay period (once)
    // so the save confirmation can name the affected period.
    const startNameEdit = async (employee: any) => {
        setEditingEmployeeId(employee.id);
        setDraftName(employee.fullName ?? '');
        if (!recentPayPeriod) {
            try {
                const res = await getApi('/pay-periods/most-recent', {
                    companyId: localStorage.getItem('companyId') || '',
                });
                if (res.data?.data) {
                    setRecentPayPeriod(res.data.data);
                }
            } catch {
                // Non-fatal — the confirmation falls back to a generic message.
            }
        }
    };

    const cancelNameEdit = () => {
        setEditingEmployeeId(null);
        setDraftName('');
    };

    // Commit a name change. Validates non-empty, no-ops when unchanged, then shows
    // a confirmation stating the most recent pay period date range before the PUT
    // fires (DQ#5 two-step UX).
    const handleNameSave = (employee: any) => {
        const trimmed = draftName.trim();
        if (!trimmed) {
            toastText('Employee name cannot be empty', 'error');
            return;
        }
        if (trimmed === employee.fullName) {
            cancelNameEdit();
            return;
        }

        // ENG-413: open the controlled confirmation dialog. Because visibility is
        // derived from `nameConfirm` state, calling this repeatedly (onClick +
        // onPressEnter, a re-render, a re-trigger after close) only ever shows the
        // one dialog — it cannot stack or reopen the way Modal.confirm() did.
        setNameConfirm({ employee, trimmedName: trimmed });
    };

    // ENG-413: commit the confirmed name change. Same dispatch the old
    // Modal.confirm onOk performed; on success clear the inline editor, and always
    // close the dialog afterward. `nameSaving` keeps the OK button in its loading
    // state for the duration of the PUT.
    const handleNameConfirmOk = async () => {
        if (!nameConfirm) {
            return;
        }
        const { employee, trimmedName } = nameConfirm;
        setNameSaving(true);
        try {
            await dispatch(
                updateEmployeeAction({
                    companyId: localStorage.getItem('companyId') || '',
                    employeeId: employee.id,
                    fullName: trimmedName,
                })
            ).unwrap();
            toastText('Employee name updated', 'success');
            cancelNameEdit();
        } catch (error: any) {
            toastText(
                error?.message || 'Failed to update employee name',
                'error'
            );
        } finally {
            setNameSaving(false);
            setNameConfirm(null);
        }
    };

    // Table change handler for sorting
    const tableChangeHandler = (_: any, __: any, sorter: any) => {
        if (sorter && sorter.order) {
            const newSortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
            // Map formattedPhone to phone for API
            const sortField = sorter.field === 'formattedPhone' ? 'phone' : sorter.field;
            performSortHandler(sortField, newSortOrder);
        } else if (sorter && !sorter.order && sorter.field) {
            // Handle case when sorting is cleared - reset to default
            performSortHandler('fullName', 'asc');
        }
    };

    // Format employee data for table. WS-14: apply any local QBO link/unlink
    // override on top of the Redux row before rendering.
    const formattedData = Array.isArray(employeesData)
        ? employeesData.map((employee: any, index: number) => {
            const override = linkOverrides[employee.id];
            const merged = override ? { ...employee, ...override } : employee;
            return {
                ...merged,
                key: merged.id,
                srNo: index + 1,
                formattedPhone: merged.phone
                    ? formatPhoneNumber(merged.phone.toString())
                    : merged.phone,
            };
        })
        : [];

    return (
        <div className={'dynamic-table dynamic-table-new'} ref={tableRef}>
            <SearchAndFilter
                performSearchHandler={performSearchHandler}
                searchValue={searchValue}
                performFilterHandler={performFilterHandler}
                filterValue={filterValue}
            />
            <Table
                dataSource={formattedData}
                scroll={{ y: '50vh', x: 'max-content' }}
                pagination={false}
                className="table-global"
                onChange={tableChangeHandler}
                loading={isLoading}
                rowKey={(record: any) => record.id}
                locale={{ emptyText: <CustomEmptyState /> }}
            >
                <Column
                    title="SR No."
                    dataIndex="srNo"
                    key="srNo"
                    className="bg-white"
                    width={'10%'}
                />
                <Column
                    title="Employee Name"
                    dataIndex="fullName"
                    key="fullName"
                    className="bg-white"
                    showSorterTooltip={{ title: '' }}
                    sorter={true}
                    sortOrder={sortBy === 'fullName' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null}
                    sortDirections={['ascend', 'descend', 'ascend']}
                    sortIcon={() => {
                        if (sortBy === 'fullName') {
                            return sortOrder === 'asc' ? (
                                <SortSvgTop />
                            ) : (
                                <SortSvgBottom />
                            );
                        }
                        // Always show icon for sortable columns (inactive state)
                        return (
                            <span style={{ opacity: 0.3 }}>
                                <SortSvgTop />
                            </span>
                        );
                    }}
                    render={(value: string, data: any) => {
                        // ENG-363 (Item 4): inline-editable name cell.
                        const saving = nameUpdateLoading[data.id] || false;
                        if (editingEmployeeId === data.id) {
                            return (
                                <Spin spinning={saving} size="small">
                                    <span className="employee-name-edit">
                                        <Input
                                            size="small"
                                            autoFocus
                                            value={draftName}
                                            disabled={saving}
                                            style={{ width: 200 }}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            onPressEnter={() => handleNameSave(data)}
                                        />
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<CheckOutlined />}
                                            disabled={saving}
                                            onClick={() => handleNameSave(data)}
                                        />
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<CloseOutlined />}
                                            disabled={saving}
                                            onClick={cancelNameEdit}
                                        />
                                    </span>
                                </Spin>
                            );
                        }
                        return (
                            <span className="employee-name-cell">
                                <span>{value}</span>
                                {isEditPermission && (
                                    <Button
                                        type="text"
                                        size="small"
                                        className="employee-name-edit-icon"
                                        icon={<EditOutlined />}
                                        loading={saving}
                                        onClick={() => startNameEdit(data)}
                                    />
                                )}
                            </span>
                        );
                    }}
                />
                <Column
                    title="Email"
                    dataIndex="email"
                    key="email"
                    className="bg-white"
                    showSorterTooltip={{ title: '' }}
                    sorter={true}
                    sortOrder={sortBy === 'email' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null}
                    sortDirections={['ascend', 'descend', 'ascend']}
                    sortIcon={() => {
                        if (sortBy === 'email') {
                            return sortOrder === 'asc' ? (
                                <SortSvgTop />
                            ) : (
                                <SortSvgBottom />
                            );
                        }
                        // Always show icon for sortable columns (inactive state)
                        return (
                            <span style={{ opacity: 0.3 }}>
                                <SortSvgTop />
                            </span>
                        );
                    }}
                />
                <Column
                    title="Phone No."
                    dataIndex="formattedPhone"
                    key="phone"
                    className="bg-white"
                    showSorterTooltip={{ title: '' }}
                    sorter={true}
                    sortOrder={sortBy === 'phone' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null}
                    sortDirections={['ascend', 'descend', 'ascend']}
                    sortIcon={() => {
                        if (sortBy === 'phone') {
                            return sortOrder === 'asc' ? (
                                <SortSvgTop />
                            ) : (
                                <SortSvgBottom />
                            );
                        }
                        // Always show icon for sortable columns (inactive state)
                        return (
                            <span style={{ opacity: 0.3 }}>
                                <SortSvgTop />
                            </span>
                        );
                    }}
                />
                <Column
                    title="Linked QBO Employee"
                    dataIndex="employeeId"
                    key="linkedQboEmployee"
                    className="bg-white"
                    align="left"
                    render={(_: any, data: any) => {
                        // A linked employee has a non-empty QBO id. '' (manual
                        // create, never linked) and null (explicitly unlinked) both
                        // read as "Not linked".
                        const linked = Boolean(data.employeeId);
                        return (
                            <div className="linked-qbo-cell" style={{ textAlign: 'left' }}>
                                {linked ? (
                                    <Tag color="blue">{data.fullName}</Tag>
                                ) : (
                                    <span style={{ color: '#999' }}>Not linked</span>
                                )}
                                {isEditPermission && (
                                    <div style={{ marginTop: 4 }}>
                                        {linked ? (
                                            <Popconfirm
                                                title="Unlink this employee from QBO? The employee record in CAP will be preserved."
                                                okText="Unlink"
                                                cancelText="Cancel"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => handleUnlink(data)}
                                            >
                                                <Button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                    loading={!!linkLoading[data.id]}
                                                >
                                                    Unlink
                                                </Button>
                                            </Popconfirm>
                                        ) : linkingRowId === data.id ? (
                                            <Select
                                                style={{ width: 220 }}
                                                size="small"
                                                showSearch
                                                autoFocus
                                                defaultOpen
                                                loading={qboLoading}
                                                placeholder="Select QBO employee…"
                                                options={qboEmployees.map((e) => ({
                                                    value: e.Id,
                                                    label: e.DisplayName,
                                                }))}
                                                filterOption={(input, option) =>
                                                    String(option?.label ?? '')
                                                        .toLowerCase()
                                                        .includes(input.toLowerCase())
                                                }
                                                onChange={(value: string) =>
                                                    handleLink(data, value)
                                                }
                                                onBlur={() => setLinkingRowId(null)}
                                            />
                                        ) : (
                                            <Button
                                                type="link"
                                                size="small"
                                                loading={!!linkLoading[data.id]}
                                                onClick={() => startLinking(data.id)}
                                            >
                                                Link to QBO
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
                {isEditPermission && (
                    <Column
                        title="Active"
                        dataIndex="isActiveInApp"
                        key="active"
                        className="bg-white"
                        width={'10%'}
                        render={(value: boolean, data: any) => {
                            const isLoading = statusUpdateLoading[data.id] || false;
                            return (
                                <span className="switch-loader-wrapper">
                                    <Spin spinning={isLoading} size="small">
                                        <Switch
                                            checked={value}
                                            disabled={!isEditPermission || isLoading}
                                            onChange={(checked) => handleStatusUpdate(checked, data)}
                                        />
                                    </Spin>
                                </span>
                            );
                        }}
                    />
                )}
            </Table>
            {total > 0 && (
                <div className="table-footer-note">
                    <div className="total-employees">Total Employees: {total}</div>
                    <div className="status-note">
                        Note: Active status only controls visibility in CostAllocation Pro. It has no impact on their QuickBooks status.
                    </div>
                </div>
            )}
            {/* ENG-413: controlled confirmation for inline name edits. Replaces the
                imperative Modal.confirm() so a repeated save trigger can never stack
                or reopen a second dialog. */}
            <Modal
                open={nameConfirm !== null}
                title="Update employee name?"
                okText="Update"
                cancelText="Cancel"
                confirmLoading={nameSaving}
                maskClosable={false}
                cancelButtonProps={{ disabled: nameSaving }}
                onOk={handleNameConfirmOk}
                onCancel={() => setNameConfirm(null)}
            >
                {nameConfirm && (
                    <p>
                        {`"${nameConfirm.employee.fullName}" will be renamed to "${nameConfirm.trimmedName}". `}
                        {recentPayPeriod
                            ? `The most recent pay period (${formatDate(
                                  recentPayPeriod.startDate
                              )} – ${formatDate(
                                  recentPayPeriod.endDate
                              )}) will reflect this change.`
                            : 'The most recent pay period will reflect this change.'}
                    </p>
                )}
            </Modal>
        </div>
    );
};

export default EmployeesTable;

